import React, { useEffect, useRef, memo } from "react";
import { Bar, Line, Doughnut, Pie } from "react-chartjs-2";
import {
  Chart as ChartJS,
  BarElement,
  LineElement,
  ArcElement,
  CategoryScale,
  LinearScale,
  PointElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";

// Register required chart elements
ChartJS.register(BarElement, LineElement, ArcElement, CategoryScale, LinearScale, PointElement, Title, Tooltip, Legend);

const ChartCreate = memo(({ chartData, labelFormat, pixelRatio }) => {
  const chartInstanceRef = useRef(null);

  Tooltip.positioners.cursorPosition = function (elements, eventPosition) {
    return {
      x: eventPosition.x,
      y: eventPosition.y,
    };
  };

  useEffect(() => {
    return () => {
      if (chartInstanceRef.current) {
        chartInstanceRef.current.destroy();
      }
    };
  }, []);

  if (!chartData?.data || !chartData?.options) return null;

  const chartProps = {
    data: chartData.data,
    options: {
      ...chartData.options,
      devicePixelRatio : pixelRatio ?? 4, 
      plugins: {
        ...chartData.options.plugins,
        tooltip: {
          ...chartData.options.plugins.tooltip,
          callbacks: {
            ...chartData.options.plugins.tooltip?.callbacks,
            label: function (context) {
              let value;
              const chartType = chartData.type;

              if (chartType === "bar" && chartData.options.indexAxis === "y") {
                value = context.parsed.x;
              } else if (chartType === "bar" || chartType === "line") {
                value = context.parsed.y;
              } else {
                value = context.raw;
              }
              const label = context.dataset.label || "";
              return `${label}: ${value}`;
            },
          },
        },
        datalabels: {
          ...chartData.options.plugins.datalabels,
          formatter: (value, context) => {
            if ((chartData.type === "pie" || chartData.type=== "doughnut") && labelFormat?.name === "Percentage") {
              const data = context.dataset.data;
              const validData = data.filter(num => typeof num === "number" && !isNaN(num));
              const total = validData.reduce((acc, num) => acc + num, 0);
              if (!total || typeof value !== "number" || isNaN(value)) return "";
              return `${((value / total) * 100).toFixed(2)}%`;
            }
            return value;
          },
        },
        legend: {
          ...chartData.options.plugins.legend,
          onClick: (e, legendItem, legend) => {},
        },
      },
    },
    ref: node => {
      if (node) {
        chartInstanceRef.current = node.chartInstance || node.chart;
      }
    },
  };

  switch (chartData.type) {
    case "bar":
      return <Bar {...chartProps} />;
    case "line":
      return <Line {...chartProps} />;
    case "doughnut":
      return <Doughnut {...chartProps} />;
    case "pie":
      return <Pie {...chartProps} />;
    default:
      return <div>Unsupported chart type</div>;
  }
});

ChartCreate.displayName = "ChartCreate";

export default ChartCreate;
